import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "~/services/api";
import { useTicketUnread } from "./useTicketUnread";
import { Attachments, AttachmentGrid, C, EVENT_ICON, Icon, IconButton, MODULES, StatusChip, buildFormData, errorMessage, formatDate, inputStyle, ResolvedAtField, WorkedField, nowLocal, splitMinutes, elapsedMinutes, toMinutes, useFileDrop } from "./tickets";

const PANELS = {
  rejected: { title: "Reject", icon: "ThumbsDown", color: C.red, url: "decision", fields: { decision: "rejected" }, key: "reason", required: true },
  changes_requested: { title: "Request changes", icon: "Pencil", color: C.amber, url: "decision", fields: { decision: "changes_requested" }, key: "reason", required: true },
  version: { title: "New version", icon: "CopyPlus", color: C.primary, url: "versions", fields: {}, key: "description", required: true, withReason: true },
  close: { title: "Close ticket", icon: "CircleCheck", color: C.green, url: "transition", fields: { action: "close" }, key: "note", required: false, json: true, time: true },
};

function EventRow({ event }) {
  const [name, color] = EVENT_ICON[event.action] || ["Circle", C.textMid];
  return (
    <div style={{ display: "flex", gap: 8 }}>
      <div style={{ width: 22, height: 22, borderRadius: 99, background: C.white, border: `1px solid ${color}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon name={name} size={12} color={color} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, color: C.textDark }}>
          <strong>{event.actor?.name}</strong>
          {event.action === "start" && <span style={{ marginLeft: 6, color: C.textMid }}>started working on the ticket</span>}
          {event.action === "assign" && <span style={{ marginLeft: 6, color: C.textMid }}>took over the ticket</span>}
          {event.action === "escalate" && <span style={{ marginLeft: 6, color: C.textMid }}>escalated the ticket to a higher level for a solution</span>}
          {event.action === "return" && <span style={{ marginLeft: 6, color: C.textMid }}>sent the ticket back to a previous level</span>}
          {event.to_status && event.to_status !== event.from_status && <span style={{ marginLeft: 6 }}><StatusChip status={event.to_status} /></span>}
        </div>
        <div style={{ fontSize: 10, color: C.textMid }}>{formatDate(event.created_at)}</div>
        {event.note && <div style={{ fontSize: 12, color: C.textDark, marginTop: 2, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{event.note}</div>}
        <AttachmentGrid items={event.attachments} />
      </div>
    </div>
  );
}

export default function TicketDetail({ id, onBack, onChanged }) {
  const [ticket, setTicket] = useState(null);
  const [versionNumber, setVersionNumber] = useState(null);
  const [panel, setPanel] = useState(null);
  const [text, setText] = useState("");
  const [reason, setReason] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [resolvedAt, setResolvedAt] = useState(nowLocal);
  const [worked, setWorked] = useState({ hours: "", minutes: "" });
  const drop = useFileDrop(setFiles);

  const load = useCallback(() => api.get(`/tickets/${id}`).then(({ data }) => { setTicket(data); setVersionNumber((v) => v ?? data.progress.version); useTicketUnread.getState().refresh(); }).catch((e) => toast.error(errorMessage(e))), [id]);
  useEffect(() => { load(); }, [load]);

  function reset() { setPanel(null); setText(""); setReason(""); setFiles([]); setResolvedAt(nowLocal()); setWorked({ hours: "", minutes: "" }); }

  async function send(url, body, { json, files: own } = {}) {
    if (busy) return false;
    setBusy(true);
    try {
      await api.post(`/tickets/${id}/${url}`, json ? body : buildFormData(body, own ?? files));
      reset();
      await load();
      onChanged?.();
      return true;
    } catch (e) {
      toast.error(errorMessage(e));
      return false;
    } finally {
      setBusy(false);
    }
  }

  if (!ticket) return <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", background: C.secondary }}><Icon name="Loader" size={18} color={C.orange} /></div>;

  const { actions, progress } = ticket;
  const version = ticket.versions.find((v) => v.version_number === versionNumber) || ticket.versions[ticket.versions.length - 1];
  const votes = ticket.approvals.filter((a) => a.version_id === ticket.current_version_id);
  const moduleLabel = MODULES.find((m) => m.value === ticket.module)?.label;
  const current = panel && PANELS[panel];

  function resolveNow() {
    const now = nowLocal();
    setResolvedAt(now);
    setWorked(splitMinutes(elapsedMinutes(ticket.created_at, now)));
  }

  function submitPanel() {
    const value = text.trim();
    if (current.required && value.length < 3) return toast.error("Write a few words first.");
    if (current.json) {
      const closing = {};
      if (current.time) {
        if (Number.isNaN(new Date(resolvedAt).getTime())) return toast.error("Choose when it was resolved.");
        const workMinutes = toMinutes(worked);
        if (workMinutes < 1) return toast.error("Inform the time spent on the solution.");
        if (workMinutes > (new Date(resolvedAt) - new Date(ticket.created_at)) / 60000) return toast.error("The time on the solution cannot be longer than the elapsed time.");
        closing.resolved_at = new Date(resolvedAt).toISOString();
        closing.work_minutes = workMinutes;
      }
      return send(current.url, { ...current.fields, [current.key]: value, ...closing }, { json: true });
    }
    return send(current.url, { ...current.fields, [current.key]: value, ...(current.withReason ? { reason } : {}) });
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, background: C.secondary }}>
      <div style={{ flex: 1, overflowY: "auto", padding: 12, display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <IconButton name="ArrowLeft" title="Back" onClick={onBack} />
          <Icon name={ticket.type === "bug" ? "Bug" : "Lightbulb"} size={16} color={C.orange} />
          <strong style={{ fontSize: 13, color: C.primary }}>{ticket.code}</strong>
          <span style={{ marginLeft: "auto" }}><StatusChip status={ticket.status} /></span>
        </div>

        <div style={{ background: C.white, border: `1px solid ${C.secondaryDark}`, borderRadius: 12, padding: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.textDark }}>{ticket.title}</div>
          <div style={{ fontSize: 10, color: C.textMid, margin: "2px 0 8px" }}>{moduleLabel} · {ticket.reporter?.name} · {formatDate(ticket.created_at)}</div>

          {ticket.versions.length > 1 && (
            <div style={{ display: "flex", gap: 4, marginBottom: 6 }}>
              {ticket.versions.map((v) => (
                <button key={v.id} type="button" onClick={() => setVersionNumber(v.version_number)} style={{ fontSize: 10, padding: "2px 8px", borderRadius: 99, cursor: "pointer", border: `1px solid ${C.primary}`, background: v.id === version.id ? C.primary : C.white, color: v.id === version.id ? C.white : C.primary }}>
                  v{v.version_number}
                </button>
              ))}
            </div>
          )}
          {version?.reason && <div style={{ fontSize: 11, color: C.textMid, fontStyle: "italic", marginBottom: 4 }}>{version.reason}</div>}
          <div style={{ fontSize: 12, color: C.textDark, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{version?.description}</div>
        </div>

        {["pending_approval", "changes_requested"].includes(ticket.status) && (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }} title={`${progress.approved} of ${progress.required} approvals`}>
            {Array.from({ length: progress.required }).map((_, i) => (
              <span key={i} style={{ width: 18, height: 18, borderRadius: 99, display: "flex", alignItems: "center", justifyContent: "center", background: i < progress.approved ? C.green : C.white, border: `1px solid ${i < progress.approved ? C.green : C.secondaryDark}` }}>
                {i < progress.approved && <Icon name="Check" size={11} color="#fff" />}
              </span>
            ))}
            <span style={{ fontSize: 10, color: C.textMid }}>{votes.filter((v) => v.decision === "approved").map((v) => v.approver?.name).join(", ")}</span>
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {ticket.events.filter((event) => !event.internal || ["escalate", "return"].includes(event.action)).map((event) => <EventRow key={event.id} event={["escalate", "return"].includes(event.action) ? { ...event, note: null } : event} />)}
        </div>
      </div>

      {current && (
        <div {...drop.handlers} style={{ padding: 12, borderTop: `1px solid ${C.secondaryDark}`, background: C.white, display: "flex", flexDirection: "column", gap: 8, outline: drop.over ? `2px dashed ${C.orange}` : "none", outlineOffset: -4 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, color: current.color }}>
            <Icon name={current.icon} size={14} />{current.title}
            <span style={{ marginLeft: "auto" }}><IconButton name="X" title="Cancel" onClick={reset} size={14} /></span>
          </div>
          {current.time && (
            <>
              <ResolvedAtField value={resolvedAt} onChange={setResolvedAt} onNow={resolveNow} openedAt={ticket.created_at} />
              <WorkedField value={worked} onChange={setWorked} />
            </>
          )}
          {current.withReason && <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why?" style={inputStyle} />}
          <textarea
            autoFocus
            rows={3}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={current.required ? "Reason" : "Message (optional)"}
            style={{ ...inputStyle, resize: "none" }}
          />
          {!current.json && <Attachments files={files} setFiles={setFiles} />}
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <IconButton name="Send" title="Confirm" solid color={current.color} disabled={busy} onClick={submitPanel} />
          </div>
        </div>
      )}

      {!current && (
        <div style={{ padding: 10, borderTop: `1px solid ${C.secondaryDark}`, background: C.white, display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {actions.approve && <IconButton name="ThumbsUp" title="Approve" color={C.green} solid disabled={busy} onClick={() => send("decision", { decision: "approved" })} />}
            {actions.approve && <IconButton name="ThumbsDown" title="Reject" color={C.red} onClick={() => setPanel("rejected")} />}
            {actions.approve && <IconButton name="Pencil" title="Request changes" color={C.amber} onClick={() => setPanel("changes_requested")} />}
            {actions.start && <IconButton name="Play" title="Start" color={C.primary} solid disabled={busy} onClick={() => send("transition", { action: "start" }, { json: true })} />}
            {actions.close && <IconButton name="CircleCheck" title="Close ticket" color={C.green} solid disabled={busy} onClick={() => { resolveNow(); setPanel("close"); }} />}
            {actions.version && <IconButton name="CopyPlus" title="New version" onClick={() => setPanel("version")} />}
            {actions.archive && <IconButton name="Archive" title="Archive" color={C.textMid} disabled={busy} onClick={() => send("transition", { action: "archive" }, { json: true })} />}
          </div>
          {actions.comment && <CommentBox busy={busy} sendWith={send} />}
        </div>
      )}
    </div>
  );
}

function CommentBox({ busy, sendWith }) {
  const [note, setNote] = useState("");
  const [files, setFiles] = useState([]);
  const drop = useFileDrop(setFiles);
  async function submit() {
    if (!note.trim() && !files.length) return;
    if (await sendWith("comments", { note: note.trim() }, { files })) {
      setNote("");
      setFiles([]);
    }
  }
  return (
    <div {...drop.handlers} style={{ display: "flex", flexDirection: "column", gap: 6, outline: drop.over ? `2px dashed ${C.orange}` : "none", outlineOffset: 2 }}>
      {files.length > 0 && <Attachments files={files} setFiles={setFiles} />}
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        {files.length === 0 && <Attachments files={files} setFiles={setFiles} />}
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          placeholder="Comment"
          style={inputStyle}
        />
        <IconButton name="Send" title="Send" solid color={C.orange} disabled={busy} onClick={submit} />
      </div>
    </div>
  );
}
